import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import type { AlertMessage } from 'src/app/shared';
import { LoggerService } from 'src/app/shared';
import type { UserNotificationPreference, UserNotificationPreferenceUpdate } from './shared/user-notification';
import { UserNotificationService } from './shared/user-notification.service';
import { AuthService } from 'src/app/auth.service';
import { environment } from 'src/environments/environment';
import type { UserProfileInfo } from '../user-profile/shared/user-profile';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Inject } from '@angular/core';
import type { Subscription } from 'rxjs';

/**
 * Display order for the notification types. Which types are shown comes from the API (only the ones the user may
 * set in the current org); a type not listed here is shown after these.
 */
const TYPE_ORDER: string[] = [
  'avl_camera_loss',
  'avl_disk_unable',
  'avl_panic_event',
  'avl_geo_fencing',
  'avl_blue_light',
  'supervisor_alert',
];

/** One row in the modal */
interface NotifRow extends UserNotificationPreference {

  /** i18n key for the type's name */
  label: string;
}

/**
 * Modal for editing the signed-in user's per-notification-type email / SMS preferences. The rows are exactly the
 * types the API lists for the current org, and only those are posted back. SMS toggles are only enabled with a
 * verified phone, and selecting any SMS notification requires an explicit consent check, which is sent with each
 * SMS opt-in. Opened from the header user menu (gated on `AVL_VIEW_EVENTS` or `DA_VIEW_ALERTS`).
 */
@Component({
  selector: 'mtx-notification-edit-modal',
  standalone: false,
  templateUrl: './notification-edit-modal.component.html',
  styleUrls: ['./notification-edit-modal.component.scss'],
})
export class NotificationEditModalComponent implements OnInit, OnDestroy {

  /** Displays error/success messages */
  public messages: AlertMessage[] = [];

  /** Need environment for auth */
  public environment = environment;

  /** One row per notification type the user may set */
  public notifs: NotifRow[] = [];

  /** SMS consent checkbox state */
  public smsConsent = false;

  public userInfo?: UserProfileInfo;

  /** Whether the preferences request is still in flight */
  public loading = true;

  /** Whether the preferences have loaded; until then (or if loading fails) there is nothing to show or save */
  public loaded = false;

  /** Whether a save is in flight */
  public saving = false;

  private loadingSub?: Subscription;

  private updateSub?: Subscription;

  public constructor(

    /** Modal reference */
    @Inject(NgbActiveModal) public activeModal: NgbActiveModal,
    @Inject(LoggerService) private readonly loggerService: LoggerService,
    @Inject(AuthService) private readonly authService: AuthService,
    @Inject(UserNotificationService) private readonly userNotificationService: UserNotificationService,
  ) {}

  /**
   * Loads the user's notification preferences
   */
  public ngOnInit(): void {
    this.userInfo = this.authService.getUserInfo();

    this.loadingSub = this.userNotificationService.search().subscribe({
      next: res => {
        this.setRows(res);
        this.loaded = true;
        this.loading = false;
      },
      error: (err: any) => {
        this.messages = this.loggerService.apiErrorsToDisplayErrors(err);
        this.loading = false;
      },
    });
  }

  public ngOnDestroy(): void {
    this.loadingSub?.unsubscribe();
    this.updateSub?.unsubscribe();
  }

  /**
   * Makes request to have notification preferences saved
   */
  public save(): void {
    if (this.saving || this.notifs.length === 0) {
      return;
    }

    this.messages = [];

    if (this.anyMobile() && !this.smsConsent) {
      return;
    }

    const payload: UserNotificationPreferenceUpdate[] = this.notifs.map(row => ({
      notification_type: row.notification_type,
      send_email: row.send_email,
      send_mobile: row.send_mobile,
      sms_consent: row.send_mobile && this.smsConsent,
    }));

    this.saving = true;
    this.updateSub = this.userNotificationService.update(payload).subscribe({
      next: () => {
        this.saving = false;
        this.activeModal.close();
      },
      error: (err: any) => {
        this.saving = false;
        this.messages = this.loggerService.apiErrorsToDisplayErrors(err);
      },
    });
  }

  /**
   * Toggles email notifications for a type
   * @param row Notification row
   */
  public toggleEmailNotification(row: NotifRow): void {
    row.send_email = !row.send_email;
  }

  /**
   * Toggles mobile notifications for a type; clears consent once no SMS notification is selected
   * @param row Notification row
   */
  public toggleMobileNotification(row: NotifRow): void {
    row.send_mobile = !row.send_mobile;

    if (!this.anyMobile()) {
      this.smsConsent = false;
    }
  }

  public anyEmail(): boolean {
    return this.notifs.some(x => x.send_email);
  }

  public anyMobile(): boolean {
    return this.notifs.some(x => x.send_mobile);
  }

  /**
   * One row per type the API listed, in display order; force SMS off where the API says SMS can't be set
   * @param prefs Preferences from the API
   */
  private setRows(prefs: UserNotificationPreference[]): void {
    const rank = (type: string): number => {
      const index = TYPE_ORDER.indexOf(type);
      return index === -1 ? TYPE_ORDER.length : index;
    };

    this.notifs = [...prefs]
      .sort((a, b) => rank(a.notification_type) - rank(b.notification_type))
      .map(pref => ({
        ...pref,
        label: `notifications.types.${pref.notification_type}`,
        send_mobile: pref.can_set_sms ? pref.send_mobile : false,
      }));
  }

}
